'use client'

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react'

import {
  useAddressListQuery,
  getAddressByCoord,
  getCurrentLocation,
  type IAddressDocument,
  useCurrentLocationStore,
} from '@/entities/place'
import { logEvent } from '@/shared/external/firebase/analytics'
import { EVENT_NAMES, LABELS } from '@/shared/lib/analytics'
import { useDebounce } from '@/shared/model'

const useSearchAddress = () => {
  const [activeSearchKeyword, setActiveSearchKeyword] = useState<string>('')
  const [currentAddressInputValue, setCurrentAddressInputValue] =
    useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    data: addressListData,
    hasNextPage: hasMoreAddresses,
    isFetchingNextPage: isLoadingMoreAddresses,
    isError: hasSearchError,
    fetchNextPage: loadMoreAddresses,
    refetch: retryAddressListFetch,
    isLoading: isLoadingAddressList,
  } = useAddressListQuery({
    keyword: activeSearchKeyword,
  })

  useEffect(() => {
    if (addressListData) {
      logEvent(EVENT_NAMES.WEB_VIEW.SEO.SHOW.SEARCH_ITEM, {
        label: LABELS.WEB_VIEW.SEO.SHOW.ADDRESS_LIST,
      })
    }
  }, [addressListData])

  const { setCurrentLocationState } = useCurrentLocationStore()

  const updateActiveSearchKeyword = useCallback((newKeyword: string) => {
    setActiveSearchKeyword(newKeyword)
  }, [])

  const debouncedAddressSearch = useDebounce({
    callback: updateActiveSearchKeyword,
    delay: 300,
  })

  const handleChangeAddressInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newAddressValue = event.target.value

      debouncedAddressSearch(newAddressValue)
      setCurrentAddressInputValue(newAddressValue)
    },
    [debouncedAddressSearch]
  )

  const handleClearAddressInput = useCallback(() => {
    setCurrentAddressInputValue('')
    setActiveSearchKeyword('')

    logEvent(EVENT_NAMES.WEB_VIEW.SEO.CLICK.SEARCH_ITEM, {
      label: LABELS.WEB_VIEW.SEO.CLICK.SEARCH_ADDRESS_KEYWORD_INPUT_RESET,
    })
  }, [])

  const handleAddressSelection = useCallback(
    (selectedAddressData: IAddressDocument) => {
      const parsedCoordinates = {
        latitude: Number(selectedAddressData.y),
        longitude: Number(selectedAddressData.x),
      }

      setCurrentLocationState({
        ...parsedCoordinates,
        addressName: selectedAddressData.address_name,
      })

      logEvent(EVENT_NAMES.WEB_VIEW.SEO.CLICK.SEARCH_ITEM, {
        label: LABELS.WEB_VIEW.SEO.CLICK.ADDRESS_LIST,
      })
    },
    [setCurrentLocationState]
  )

  const handleRetryAddressSearch = useCallback(() => {
    retryAddressListFetch()
  }, [retryAddressListFetch])

  const handleClickCurrentLocation = useCallback(async () => {
    const currentLocation = await getCurrentLocation()

    if (currentLocation.isAccessDenied) {
      setCurrentLocationState({
        isAccessDenied: true,
      })
      return
    }

    const currentAddressByCoordData = await getAddressByCoord({
      longitude: currentLocation.longitude,
      latitude: currentLocation.latitude,
    })

    const newKeyword =
      currentAddressByCoordData?.documents[0]?.road_address?.address_name
    setCurrentAddressInputValue(newKeyword ?? '')
    setActiveSearchKeyword(newKeyword ?? '')

    logEvent(EVENT_NAMES.WEB_VIEW.SEO.CLICK.SEARCH_ITEM, {
      label: LABELS.WEB_VIEW.SEO.CLICK.CURRENT_LOCATION,
    })
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  return {
    state: {
      currentValue: currentAddressInputValue,
      hasMoreAddresses: hasMoreAddresses ?? false,
      isLoadingMoreAddresses,
      isLoadingAddressList,
      hasSearchError,
      inputRef,
    },
    data: {
      addressDocuments: addressListData?.documents ?? null,
    },
    actions: {
      handleAddressSelection,
      handleClickCurrentLocation,
      handleChangeInput: handleChangeAddressInput,
      handleClearInput: handleClearAddressInput,
      loadMoreAddresses,
      retryAddressSearch: handleRetryAddressSearch,
    },
  }
}

export default useSearchAddress
